/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Output } from "valibot";

import { Message, MessageType } from "components/Bubble/Message";
import { createTicketHandOff, getHumanHandoffSummary } from "shared/api";
import { useConfigContext } from "shared/hooks";
import { IntegrationHandler } from "shared/integrations";
import { LocaleSchema } from "shared/schemas";
import { isValidEmail } from "shared/utils/isValidEmail";

export interface IZenDeskOrFreshDeskProps {
  integration: IntegrationHandler;
  generationLanguage: Output<typeof LocaleSchema>;
  botAvatar: string;
  chatId: string;
  token: string;
  onFinish?: () => void;
  isFreshDesk?: boolean;
}

interface IRequiredUserfields {
  name: string;
  type: string;
  label: string;
  placeholder: string;
  required: boolean;
}

interface ICreateTickertInfo {
  name?: string;
  email?: string;
  summary?: string;
  subject?: string;
}
const STEP_NUMBER_THANKS_MESSAGE = 2;
const STEP_PREMESSAGE = -1;

export default function ZenDeskOrFreshDeskIntegration(
  props: IZenDeskOrFreshDeskProps,
) {
  const {
    integration,
    generationLanguage,
    botAvatar,
    chatId,
    token,
    onFinish,
    isFreshDesk,
  } = props;
  const { isIframe } = useConfigContext();
  const [currentEditingIndex, setCurrentEditingIndex] = useState<number>(-1);
  const [createTicketInfo, setCreateTicketInfo] = useState<ICreateTickertInfo>(
    {},
  );
  const integrationData = JSON.parse(
    integration?.required_user_fields ?? "",
  ) as IRequiredUserfields[];

  useEffect(() => {
    if (STEP_PREMESSAGE !== currentEditingIndex) {
      return;
    }
    const increaseIndexTimeout = setTimeout(() => {
      increaseCurrentIndex();
    }, 500);

    return () => {
      clearTimeout(increaseIndexTimeout);
    };
  }, []);
  const increaseCurrentIndex = () => {
    setCurrentEditingIndex(
      prevCurrentEditingIndex => prevCurrentEditingIndex + 1,
    );
  };

  const hanleOnLastMessageSubmit = async (name: string, email: string) => {
    try {
      const summaryData = await getHumanHandoffSummary({ chatId, token });
      if (!summaryData) {
        throw new Error("Something went wrong, please try again later");
      }
      const { subject, summary } = summaryData;
      if (!name || !email) {
        throw new Error("Something went wrong, please try again later");
      }
      const createTicket = await createTicketHandOff({
        email,
        name,
        subject,
        summary,
        token,
      });
      if (createTicket === null) {
        increaseCurrentIndex();
        onFinish && onFinish();
      } else {
        throw new Error("Something went wrong, please try again later");
      }
    } catch (err: any) {
      return new Error(err);
    }
  };

  const handleOnMessageSubmit = (id: string, value: string) => {
    if (id === "name") {
      const nameInputValue = value.trim();
      //validation
      if (nameInputValue === "") {
        return new Error("Name can not be empty");
      } else if ([...nameInputValue].length <= 3) {
        return new Error("Name can not less than 3 letter");
      } else {
        setCreateTicketInfo(oldVal => {
          return { ...oldVal, name: nameInputValue };
        });
        increaseCurrentIndex();
      }
    } else if (id === "email") {
      const emailInputValue = value.trim();
      if (emailInputValue === "") {
        return new Error("Email can not be empty");
      } else if (isValidEmail(emailInputValue)) {
        setCreateTicketInfo(oldVal => {
          return { ...oldVal, email: emailInputValue };
        });
        return hanleOnLastMessageSubmit(
          createTicketInfo.name ?? "",
          emailInputValue,
        );
      } else {
        return new Error("Email is not valid");
      }
    }
    return;
  };
  return (
    <div className="flex flex-col gap-3" id="zendeskIntegrationDiv">
      {currentEditingIndex >= STEP_PREMESSAGE && (
        <Message
          generationLanguage={generationLanguage}
          id="innital_summary"
          type="text_only"
          value={
            isFreshDesk
              ? "Sure, we'll create a new Freshdesk ticket for your issue. Could you help us with some more information?"
              : "Sure, we'll create a new Zendesk ticket for your issue. Could you help us with some more information?"
          }
          isRequired={true}
          botAvatar={botAvatar}
          isIframe={isIframe}
        />
      )}
      {integrationData.map((data, idx) => {
        const { label, name, placeholder, required, type } = data;
        if (!(idx <= currentEditingIndex)) {
          return null;
        }
        return (
          <Message
            generationLanguage={generationLanguage}
            id={name}
            isNonEditable={idx !== currentEditingIndex}
            onSubmit={handleOnMessageSubmit}
            type={type as MessageType}
            value=""
            placeholder={placeholder}
            label={label}
            isRequired={required}
            botAvatar={botAvatar}
            isIframe={isIframe}
          />
        );
      })}
      {currentEditingIndex >= STEP_NUMBER_THANKS_MESSAGE && (
        <>
          <Message
            generationLanguage={generationLanguage}
            id="end_message_1"
            type="text_only"
            value="Thank you, your ticket is successfully created. Someone from the team will reach out to you shortly."
            isRequired
            botAvatar={botAvatar}
            isIframe={isIframe}
          />
        </>
      )}
    </div>
  );
}
